import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Spinner from '../components/Spinner';
import DraggableLinkItem from '../components/DraggableLinkItem';
import LayoutBlockManager from '../components/LayoutBlockManager';
import IconPicker from '../components/IconPicker';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { ICONS } from '../lib/icons';

const {
  FiLogOut,
  FiPlus,
  FiSettings,
  FiLayout,
  FiCheck,
  FiAlertCircle,
  FiCopy,
  FiUpload,
  FiImage,
  FiX,
  FiExternalLink,
  FiLink2
} = FiIcons;

const THEMES = [
  { name: 'Blue', color: '#3B82F6' },
  { name: 'Purple', color: '#8B5CF6' },
  { name: 'Pink', color: '#EC4899' },
  { name: 'Green', color: '#10B981' },
  { name: 'Orange', color: '#F59E0B' },
  { name: 'Black', color: '#111827' }
];

const FONTS = [
  { name: 'Manrope', class: 'font-manrope' },
  { name: 'Inter', class: 'font-inter' },
  { name: 'Poppins', class: 'font-poppins' },
  { name: 'Roboto', class: 'font-roboto' },
  { name: 'Space Mono', class: 'font-space-mono' },
  { name: 'Playfair Display', class: 'font-playfair' }
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [links, setLinks] = useState([]);
  const [activeTab, setActiveTab] = useState('links');

  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [themeColor, setThemeColor] = useState('#3B82F6');
  const [fontFamily, setFontFamily] = useState('Manrope');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [username, setUsername] = useState('');
  const [layoutBlocks, setLayoutBlocks] = useState([
    { id: 'logo', visible: true },
    { id: 'avatar', visible: true },
    { id: 'displayName', visible: true },
    { id: 'bio', visible: true }
  ]);

  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [addingLink, setAddingLink] = useState(false);
  const [newLink, setNewLink] = useState({ title: '', url: '', icon_name: 'Link' });
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/login');
      return;
    }
    setSession(session);
    await fetchData(session.user.id);
  };

  const applyProfileData = (data) => {
    setProfile(data);
    setDisplayName(data.display_name || '');
    setDescription(data.description || '');
    setThemeColor(data.theme_color || '#3B82F6');
    setFontFamily(data.font_family || 'Manrope');
    setAvatarUrl(data.avatar_url || '');
    setLogoUrl(data.logo_url || '');
    setUsername(data.username || '');
    if (data.layout_config?.blocks) {
      setLayoutBlocks(data.layout_config.blocks);
    }
  };

  const fetchData = async (userId) => {
    setLoading(true);
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (profileError && profileError.code === 'PGRST116') {
      // Profile doesn't exist yet (first login after registration)
      const { data: userData } = await supabase.auth.getUser();
      const meta = userData?.user?.user_metadata || {};
      
      const newProfile = {
        id: userId,
        username: meta.username || `user_${userId.substring(0, 8)}`,
        full_name: meta.full_name || '',
        phone: meta.phone || '',
        display_name: '',
        description: '',
        theme_color: '#3B82F6',
        font_family: 'Manrope',
        layout_config: { blocks: [
          { id: 'logo', visible: true },
          { id: 'avatar', visible: true },
          { id: 'displayName', visible: true },
          { id: 'bio', visible: true }
        ]}
      };

      const { data: insertedProfile, error: insertError } = await supabase
        .from('profiles')
        .insert([newProfile])
        .select()
        .single();

      if (!insertError && insertedProfile) {
        applyProfileData(insertedProfile);
      } else {
        console.error('Failed to create initial profile:', insertError);
      }
    } else if (profileData) {
      applyProfileData(profileData);
    } else if (profileError) {
      console.error('Profile fetch error:', profileError);
    }

    const { data: linksData, error: linksError } = await supabase
      .from('links')
      .select('*')
      .eq('user_id', userId)
      .order('position_order', { ascending: true });

    if (!linksError) setLinks(linksData || []);
    setLoading(false);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setError('');
    setSuccess('');

    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        display_name: displayName,
        description: description,
        theme_color: themeColor,
        font_family: fontFamily,
        avatar_url: avatarUrl,
        logo_url: logoUrl,
        layout_config: { blocks: layoutBlocks },
        updated_at: new Date()
      })
      .eq('id', session.user.id);

    if (updateError) {
      setError('Error updating profile. Please try again.');
      console.error(updateError);
    } else {
      setProfile({
        ...profile,
        display_name: displayName,
        description,
        theme_color: themeColor,
        font_family: fontFamily,
        avatar_url: avatarUrl,
        logo_url: logoUrl,
        layout_config: { blocks: layoutBlocks }
      });
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    }

    setSavingProfile(false);
  };

  const handleUpdateUsername = async () => {
    if (!username || username.length < 3) {
      setError('Username must be at least 3 characters long');
      return;
    }

    const cleanUsername = username.toLowerCase().replace(/[^a-z0-9_-]/g, '');
    setSavingProfile(true);
    setError('');
    setSuccess('');

    const { error: updateError } = await supabase
      .from('profiles')
      .update({ username: cleanUsername })
      .eq('id', session.user.id);

    if (updateError) {
      if (updateError.code === '23505') {
        setError('This username is already taken. Please choose another.');
      } else {
        setError('Error updating username. Please try again.');
      }
      console.error(updateError);
    } else {
      setProfile({ ...profile, username: cleanUsername });
      setSuccess('Username updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    }

    setSavingProfile(false);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setUploadingAvatar(true);
    setError('');

    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${session.user.id}/avatar_${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file, { cacheControl: '3600', upsert: false });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      setAvatarUrl(publicUrl);

      const { error: dbError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', session.user.id);

      if (dbError) throw dbError;

      setSuccess('Avatar uploaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Avatar upload error:', error);
      setError('Failed to upload avatar. Please try again.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    setAvatarUrl('');
    const { error } = await supabase
      .from('profiles')
      .update({ avatar_url: null })
      .eq('id', session.user.id);

    if (!error) {
      setSuccess('Avatar removed successfully!');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    setError('');

    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${session.user.id}/logo_${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file, { cacheControl: '3600', upsert: false });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      setLogoUrl(publicUrl);

      const { error: dbError } = await supabase
        .from('profiles')
        .update({ logo_url: publicUrl })
        .eq('id', session.user.id);

      if (dbError) throw dbError;

      setSuccess('Logo uploaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Logo upload error:', error);
      setError('Failed to upload logo. Please try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    setLogoUrl('');
    const { error } = await supabase
      .from('profiles')
      .update({ logo_url: null })
      .eq('id', session.user.id);

    if (!error) {
      setSuccess('Logo removed successfully!');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleAddLink = async (e) => {
    e.preventDefault();
    setAddingLink(true);

    let formattedUrl = newLink.url.trim();
    if (formattedUrl && !formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = `https://${formattedUrl}`;
    }

    const { data, error } = await supabase
      .from('links')
      .insert([
        {
          user_id: session.user.id,
          title: newLink.title,
          url: formattedUrl,
          icon_name: newLink.icon_name,
          position_order: links.length,
          is_visible: true
        }
      ])
      .select();

    if (!error && data) {
      setLinks([...links, data[0]]);
      setNewLink({ title: '', url: '', icon_name: 'Link' });
    } else {
      console.error('Error adding link:', error);
      setError('Error adding link. Please try again.');
    }

    setAddingLink(false);
  };

  const handleUpdateLink = async (id, updates) => {
    const { error } = await supabase
      .from('links')
      .update(updates)
      .eq('id', id);

    if (!error) {
      setLinks(links.map(l => l.id === id ? { ...l, ...updates } : l));
    }
  };

  const handleDeleteLink = async (id) => {
    const { error } = await supabase.from('links').delete().eq('id', id);
    if (!error) {
      setLinks(links.filter(l => l.id !== id));
    }
  };

  const handleToggleVisibility = async (id, currentVisibility) => {
    const { error } = await supabase
      .from('links')
      .update({ is_visible: !currentVisibility })
      .eq('id', id);

    if (!error) {
      setLinks(links.map(l => l.id === id ? { ...l, is_visible: !currentVisibility } : l));
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = links.findIndex(l => l.id === active.id);
      const newIndex = links.findIndex(l => l.id === over.id);
      const newLinks = arrayMove(links, oldIndex, newIndex);
      setLinks(newLinks);

      const updates = newLinks.map((link, index) => ({
        id: link.id,
        position_order: index
      }));

      for (const update of updates) {
        await supabase
          .from('links')
          .update({ position_order: update.position_order })
          .eq('id', update.id);
      }
    }
  };

  const handleLayoutDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = layoutBlocks.findIndex(b => b.id === active.id);
      const newIndex = layoutBlocks.findIndex(b => b.id === over.id);
      setLayoutBlocks(arrayMove(layoutBlocks, oldIndex, newIndex));
    }
  };

  const handleToggleBlock = (blockId) => {
    setLayoutBlocks(layoutBlocks.map(b =>
      b.id === blockId ? { ...b, visible: !b.visible } : b
    ));
  };

  const handleCopyLink = () => {
    const profileUrl = `${window.location.origin}/#/${profile?.username}`;
    navigator.clipboard.writeText(profileUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenLink = () => {
    const profileUrl = `${window.location.origin}/#/${profile?.username}`;
    window.open(profileUrl, '_blank');
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size={48} />
      </div>
    );
  }

  // Determine standard font class
  const fontObj = FONTS.find(f => f.name === fontFamily) || FONTS[0];
  const fontClass = fontObj.class;

  const NewLinkIcon = ICONS[newLink.icon_name] || ICONS.Link;

  const renderProfileBlock = (block) => {
    if (!block.visible) return null;

    switch (block.id) {
      case 'logo':
        return logoUrl ? (
          <div key="logo" className="mb-6">
            <img src={logoUrl} className="h-12 object-contain" alt="Logo" />
          </div>
        ) : null;
      case 'avatar':
        return (
          <div
            key="avatar"
            className="w-20 h-20 rounded-full mb-4 shadow-lg flex items-center justify-center text-white text-2xl font-bold overflow-hidden"
            style={{ backgroundColor: themeColor }}
          >
            {avatarUrl ? (
              <img src={avatarUrl} className="w-full h-full object-cover" alt="Avatar" />
            ) : (
              (displayName || profile?.username || '?').charAt(0).toUpperCase()
            )}
          </div>
        );
      case 'displayName':
        return (
          <h3 key="displayName" className="text-xl font-extrabold text-gray-900 mb-1">
            {displayName || `@${profile?.username || 'username'}`}
          </h3>
        );
      case 'bio':
        return description ? (
          <p key="bio" className="text-sm text-gray-500 mb-8 max-w-[200px]">
            {description}
          </p>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 font-bold text-xl text-gray-900">
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 text-white p-2 rounded-xl shadow-lg">
              <SafeIcon icon={FiLink2} size={24} />
            </div>
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              MyLinks
            </span>
          </div>

          {profile?.username && (
            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
              <span className="text-xs font-medium text-gray-500 hidden sm:inline">
                {window.location.origin}/#/{profile.username}
              </span>
              <span className="text-xs font-medium text-gray-500 sm:hidden">
                /{profile.username}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={handleCopyLink}
                  className="p-2 hover:bg-white rounded-lg transition-colors text-gray-600 hover:text-blue-600"
                  title="Copy link"
                >
                  <SafeIcon icon={copied ? FiCheck : FiCopy} size={16} />
                </button>
                <button
                  onClick={handleOpenLink}
                  className="p-2 hover:bg-white rounded-lg transition-colors text-gray-600 hover:text-blue-600"
                  title="Open in new tab"
                >
                  <SafeIcon icon={FiExternalLink} size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-2 flex gap-2">
            <button
              onClick={() => setActiveTab('links')}
              className={`flex-1 py-2.5 px-4 rounded-xl font-semibold transition-all ${
                activeTab === 'links'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Links
            </button>
            <button
              onClick={() => setActiveTab('appearance')}
              className={`flex-1 py-2.5 px-4 rounded-xl font-semibold transition-all ${
                activeTab === 'appearance'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Appearance
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`flex-1 py-2.5 px-4 rounded-xl font-semibold transition-all ${
                activeTab === 'settings'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Settings
            </button>
          </div>

          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl flex items-center gap-2">
              <SafeIcon icon={FiAlertCircle} />
              {error}
            </div>
          )}

          {success && (
            <div className="p-3 bg-green-50 text-green-600 text-sm rounded-xl flex items-center gap-2">
              <SafeIcon icon={FiCheck} />
              {success}
            </div>
          )}

          {activeTab === 'links' && (
            <section className="space-y-4">
              <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
                <form onSubmit={handleAddLink} className="space-y-4">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setShowIconPicker(true)}
                      className="p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors shrink-0"
                    >
                      <NewLinkIcon size={20} className="text-blue-600" />
                    </button>
                    <input
                      type="text"
                      placeholder="Link Title"
                      value={newLink.title}
                      onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                      className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none"
                      required
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="URL (https://...)"
                    value={newLink.url}
                    onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none"
                    required
                  />
                  <button
                    type="submit"
                    disabled={addingLink}
                    className="w-full bg-gray-900 hover:bg-black text-white font-bold py-3 rounded-2xl transition-all flex items-center justify-center gap-2"
                  >
                    {addingLink ? (
                      <Spinner size={20} className="text-white" />
                    ) : (
                      <>
                        <SafeIcon icon={FiPlus} />
                        Add New Link
                      </>
                    )}
                  </button>
                </form>
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={links.map(l => l.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {links.length === 0 ? (
                      <div className="text-center py-12 bg-gray-100 rounded-3xl border-2 border-dashed border-gray-200 text-gray-400 font-medium">
                        No links yet. Add your first one above!
                      </div>
                    ) : (
                      links.map(link => (
                        <DraggableLinkItem
                          key={link.id}
                          link={link}
                          onDelete={handleDeleteLink}
                          onToggleVisibility={handleToggleVisibility}
                          onUpdate={handleUpdateLink}
                        />
                      ))
                    )}
                  </div>
                </SortableContext>
              </DndContext>
            </section>
          )}

          {activeTab === 'appearance' && (
            <section className="space-y-6">
              <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-50 font-bold text-gray-800">
                  Theme & Typography
                </div>
                <div className="p-6 space-y-6">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-3 ml-1">
                      Theme Color
                    </label>
                    <div className="flex flex-wrap gap-3">
                      {THEMES.map(t => (
                        <button
                          key={t.name}
                          onClick={() => setThemeColor(t.color)}
                          className={`w-10 h-10 rounded-full border-4 transition-all ${
                            themeColor === t.color ? 'border-gray-300 scale-110' : 'border-transparent'
                          }`}
                          style={{ backgroundColor: t.color }}
                          title={t.name}
                          type="button"
                        />
                      ))}
                      <input
                        type="color"
                        value={themeColor}
                        onChange={(e) => setThemeColor(e.target.value)}
                        className="w-10 h-10 rounded-full cursor-pointer p-0 border-none bg-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-3 ml-1">
                      Typography
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {FONTS.map(f => (
                        <button
                          key={f.name}
                          onClick={() => setFontFamily(f.name)}
                          className={`py-3 px-4 rounded-xl border-2 transition-all ${
                            fontFamily === f.name
                              ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                              : 'border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-300'
                          } ${f.class} text-sm font-semibold`}
                          type="button"
                        >
                          {f.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-50 font-bold text-gray-800">
                  Profile Information
                </div>
                <form onSubmit={handleUpdateProfile} className="p-6 space-y-5">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 ml-1">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 ml-1">
                      Bio
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                      rows="2"
                      placeholder="Tell the world about yourself..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={savingProfile}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-2xl transition-all flex items-center justify-center sm:justify-start gap-2 disabled:opacity-50"
                  >
                    {savingProfile ? (
                      <Spinner size={20} className="text-white" />
                    ) : (
                      <>
                        <SafeIcon icon={FiCheck} />
                        Save Appearance
                      </>
                    )}
                  </button>
                </form>
              </div>
            </section>
          )}

          {activeTab === 'settings' && (
            <section className="space-y-6">
              
              <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-50 font-bold text-gray-800">
                  Images
                </div>
                <div className="p-6 space-y-5">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-3 ml-1">
                      Avatar Image
                    </label>
                    <div className="flex items-center gap-4">
                      <div
                        className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold overflow-hidden border-4 border-gray-100 shrink-0"
                        style={{ backgroundColor: themeColor }}
                      >
                        {avatarUrl ? (
                          <img src={avatarUrl} className="w-full h-full object-cover" alt="Avatar" />
                        ) : (
                          <SafeIcon icon={FiImage} size={32} />
                        )}
                      </div>
                      <div className="flex-1 flex gap-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarUpload}
                          className="hidden"
                          id="avatar-upload"
                          disabled={uploadingAvatar}
                        />
                        <label
                          htmlFor="avatar-upload"
                          className={`inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl cursor-pointer transition-all ${
                            uploadingAvatar ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {uploadingAvatar ? (
                            <>
                              <Spinner size={16} />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <SafeIcon icon={FiUpload} size={16} />
                              Upload
                            </>
                          )}
                        </label>
                        {avatarUrl && (
                          <button
                            onClick={handleRemoveAvatar}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 font-semibold rounded-xl transition-all"
                          >
                            <SafeIcon icon={FiX} size={16} />
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-3 ml-1">
                      Logo Image
                    </label>
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-xl flex items-center justify-center bg-gray-50 border-2 border-gray-100 overflow-hidden shrink-0">
                        {logoUrl ? (
                          <img src={logoUrl} className="w-full h-full object-contain p-2" alt="Logo" />
                        ) : (
                          <SafeIcon icon={FiImage} size={32} className="text-gray-300" />
                        )}
                      </div>
                      <div className="flex-1 flex gap-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                          id="logo-upload"
                          disabled={uploadingLogo}
                        />
                        <label
                          htmlFor="logo-upload"
                          className={`inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl cursor-pointer transition-all ${
                            uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {uploadingLogo ? (
                            <>
                              <Spinner size={16} />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <SafeIcon icon={FiUpload} size={16} />
                              Upload
                            </>
                          )}
                        </label>
                        {logoUrl && (
                          <button
                            onClick={handleRemoveLogo}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 font-semibold rounded-xl transition-all"
                          >
                            <SafeIcon icon={FiX} size={16} />
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-50 font-bold text-gray-800">
                  Profile Layout
                </div>
                <div className="p-6">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleLayoutDragEnd}
                  >
                    <SortableContext
                      items={layoutBlocks.map(b => b.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <LayoutBlockManager
                        blocks={layoutBlocks}
                        onReorder={setLayoutBlocks}
                        onToggle={handleToggleBlock}
                      />
                    </SortableContext>
                  </DndContext>
                  <button
                    onClick={handleUpdateProfile}
                    disabled={savingProfile}
                    className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-2xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {savingProfile ? (
                      <Spinner size={20} className="text-white" />
                    ) : (
                      <>
                        <SafeIcon icon={FiCheck} />
                        Save Layout Updates
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-50 flex items-center gap-2 font-bold text-gray-800">
                  <SafeIcon icon={FiSettings} className="text-blue-600" />
                  Account Settings
                </div>
                <div className="p-6 space-y-5">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 ml-1">
                      Username
                    </label>
                    <div className="flex gap-3">
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                          @
                        </span>
                        <input
                          type="text"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          className="w-full pl-8 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none"
                          placeholder="yourname"
                          minLength={3}
                        />
                      </div>
                      <button
                        onClick={handleUpdateUsername}
                        disabled={savingProfile}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-2xl transition-all disabled:opacity-50"
                      >
                        {savingProfile ? <Spinner size={20} className="text-white" /> : 'Update'}
                      </button>
                    </div>
                  </div>

                  <div className="pt-5 border-t border-gray-100">
                    <button
                      onClick={handleSignOut}
                      className="w-full bg-red-50 hover:bg-red-100 text-red-600 font-bold py-3 px-6 rounded-2xl transition-all flex items-center justify-center gap-2"
                    >
                      <SafeIcon icon={FiLogOut} size={18} />
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>

            </section>
          )}
        </div>

        <div className="lg:col-span-5 hidden lg:block">
          <div className="sticky top-28">
            <div className="mb-4 flex items-center justify-center gap-2 text-sm font-bold text-gray-400 uppercase tracking-widest">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              Live Preview
            </div>
            {/* The fontClass is applied directly to the preview container so the chosen font is active */}
            <div className="aspect-[9/16] bg-white rounded-[3rem] border-[12px] border-gray-900 shadow-2xl overflow-hidden relative">
              <div
                className={`absolute inset-0 overflow-y-auto ${fontClass}`}
                style={{ backgroundColor: `${themeColor}10` }}
              >
                <div className="min-h-full p-8 flex flex-col items-center text-center">
                  {layoutBlocks.map(block => renderProfileBlock(block))}

                  <div className="w-full space-y-3">
                    {links.filter(l => l.is_visible).map(link => {
                      const LinkIcon = ICONS[link.icon_name] || ICONS.Link;
                      return (
                        <div
                          key={link.id}
                          className="w-full py-3.5 px-6 rounded-2xl font-bold text-sm shadow-sm border border-transparent transition-all flex items-center justify-center gap-2"
                          style={{ backgroundColor: themeColor, color: '#fff' }}
                        >
                          <LinkIcon size={18} />
                          {link.title}
                        </div>
                      );
                    })}

                    {links.filter(l => l.is_visible).length === 0 && (
                      <div className="w-full py-3.5 px-6 rounded-2xl bg-white border border-gray-100 text-gray-300 font-bold text-sm italic">
                        Links will appear here
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {showIconPicker && (
        <IconPicker
          selectedIcon={newLink.icon_name}
          onSelect={(iconName) => {
            setNewLink({ ...newLink, icon_name: iconName });
            setShowIconPicker(false);
          }}
          onClose={() => setShowIconPicker(false)}
        />
      )}
    </div>
  );
}